import type { InputProps } from './types'

import { Input as BaseUIInput } from '@base-ui/react/input'

import {
  ghostFieldClasses,
  ghostFieldInputClasses,
} from '#components/_primitives/ghost-field'
import { useFieldSize } from '#components/field'
import { textVariants } from '#components/text/styles'
import { TextColor } from '#components/text/types'
import { cn } from '#lib/cn'

import { InputContainer } from './input-container'
import { inputBaseClasses, inputSizeToTextSize } from './styles'
import { InputVariant } from './types'

/**
 * A styled text input component.
 *
 * @example
 * // Basic usage (standalone, e.g., search inputs)
 * <Input placeholder="Search..." />
 *
 * @example
 * // With Field for labels, descriptions, and errors
 * <Field>
 *   <Field.Label required>Email</Field.Label>
 *   <Field.Control>
 *     <Input placeholder="name@example.com" />
 *   </Field.Control>
 *   <Field.Description>We'll never share your email.</Field.Description>
 * </Field>
 *
 * @example
 * // With error state
 * <Field invalid>
 *   <Field.Label>Password</Field.Label>
 *   <Field.Control>
 *     <Input type="password" />
 *   </Field.Control>
 *   <Field.Error>Password is required</Field.Error>
 * </Field>
 */
export function Input({
  className,
  inputClassName,
  size,
  variant,
  invalid = false,
  disabled,
  leftSection,
  rightSection,
  ref,
  'aria-invalid': ariaInvalid,
  ...props
}: InputProps) {
  const fieldSize = useFieldSize()
  const resolvedSize = size ?? fieldSize
  const resolvedInvalid =
    invalid || ariaInvalid === true || ariaInvalid === 'true'
  const ghostActive =
    variant === InputVariant.Inline && !resolvedInvalid && !disabled

  return (
    <InputContainer
      className={cn(ghostActive && ghostFieldClasses, className)}
      disabled={disabled}
      invalid={resolvedInvalid}
      leftSection={leftSection}
      rightSection={rightSection}
      size={resolvedSize}
    >
      <BaseUIInput
        aria-invalid={ariaInvalid}
        className={cn(
          inputBaseClasses,
          textVariants({
            color: TextColor.Primary,
            size: inputSizeToTextSize[resolvedSize],
          }),
          ghostActive && ghostFieldInputClasses,
          inputClassName,
        )}
        disabled={disabled}
        ref={ref}
        {...props}
      />
    </InputContainer>
  )
}

Input.displayName = 'Input'
